import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, UserPlus, UserCheck, Users } from "lucide-react";
import { plyxApi as api } from "@/lib/plyxApi";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { toast } from "sonner";

/**
 * PlyxNetwork — `/plyx/u/:handle/network?tab=followers|following`
 *
 * Two tabs on top of a shared row list. Reused for both "people who
 * follow me" and "people I follow" so there's a single source of
 * truth for the row layout, the inline Follow-back button, and the
 * empty state.
 *
 * Linked from the profile page by tapping the follower / following
 * counts — the single fastest path to "who's my crew".
 */
const PlyxNetwork = () => {
  const { handle } = useParams();
  const navigate = useNavigate();
  const { user } = usePlyxAuth();
  const [search] = useSearchParams();
  const initialTab = search.get("tab") === "following" ? "following" : "followers";
  const [tab, setTab] = useState(initialTab);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/plyx/u/${handle}/${tab}`);
      setUsers(r.data?.users || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load list");
      setUsers([]);
    } finally { setLoading(false); }
  }, [handle, tab]);

  useEffect(() => { load(); }, [load]);

  const toggleFollow = async (u) => {
    try {
      if (u.is_following) {
        await api.delete(`/plyx/u/${u.handle}/follow`);
        u.is_following = false;
        u.follower_count = Math.max(0, (u.follower_count || 1) - 1);
        toast.message(`Unfollowed @${u.handle}`);
      } else {
        await api.post(`/plyx/u/${u.handle}/follow`);
        u.is_following = true;
        u.follower_count = (u.follower_count || 0) + 1;
        toast.success(`Following @${u.handle}`);
      }
      setUsers((prev) => [...prev]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update follow");
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white pb-24" data-testid="plyx-network-page">
      <div className="max-w-2xl mx-auto px-4 pt-4">
        <div className="flex items-center gap-2 mb-4">
          <button
            onClick={() => navigate(`/plyx/u/${handle}`)}
            aria-label="Back to profile"
            data-testid="plyx-network-back"
            className="p-2 rounded-full hover:bg-white/5"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <Users className="w-5 h-5 text-amber-300" />
          <h1 className="text-xl font-black tracking-tight">@{handle}</h1>
        </div>

        <div className="flex gap-2 mb-4 border-b border-white/10">
          {["followers", "following"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`plyx-network-tab-${t}`}
              className={
                "px-4 py-2.5 text-sm font-semibold transition border-b-2 -mb-px "
                + (tab === t
                  ? "border-amber-300 text-amber-300"
                  : "border-transparent text-white/50 hover:text-white")
              }
            >
              {t === "followers" ? "Followers" : "Following"}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-white/40 text-sm text-center mt-6">Loading…</p>
        ) : users.length === 0 ? (
          <div className="text-center mt-12 text-white/50 text-sm">
            {tab === "followers"
              ? "No followers yet — share your profile to gain your first few."
              : "Not following anyone yet — head to Discover to find members."}
            <div className="mt-3">
              <Link
                to="/plyx/discover"
                className="inline-block px-4 py-2 rounded-full bg-amber-300 text-black text-xs font-bold"
                data-testid="plyx-network-discover-cta"
              >
                Find people on Discover
              </Link>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {users.map((u) => (
              <div
                key={u.plyx_user_id}
                data-testid={`plyx-network-row-${u.handle}`}
                className="flex items-center gap-3 p-3 rounded-2xl bg-white/[0.03] border border-white/5"
              >
                <Link to={`/plyx/u/${u.handle}`} className="shrink-0">
                  {u.avatar_url ? (
                    <img src={u.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-300 to-pink-500" />
                  )}
                </Link>
                <Link to={`/plyx/u/${u.handle}`} className="flex-1 min-w-0">
                  <p className="text-sm font-bold truncate">{u.display_name || `@${u.handle}`}</p>
                  <p className="text-xs text-white/50 truncate">
                    @{u.handle} · {u.follower_count || 0} followers
                  </p>
                </Link>
                {user && user.handle !== u.handle && (
                  <button
                    type="button"
                    onClick={() => toggleFollow(u)}
                    data-testid={`plyx-network-follow-${u.handle}`}
                    className={
                      "shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition "
                      + (u.is_following
                        ? "bg-white/10 text-white border border-white/15"
                        : "bg-amber-300 text-black hover:bg-amber-200")
                    }
                  >
                    {u.is_following ? (
                      <><UserCheck className="w-3.5 h-3.5" /> Following</>
                    ) : (
                      <><UserPlus className="w-3.5 h-3.5" /> Follow back</>
                    )}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlyxNetwork;
